import { FC, PropsWithChildren, useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { CUSTOMER_DISPLAY_CHANNEL } from '@/constants/constant'

import {
  ActionTypeEnum,
  CustomerDisplayContext,
  PageTypeEnum,
  PayloadCustomerDisplay,
} from '../contexts/CustomerDisplayContext'

type Props = {}

type Listener = (data: PayloadCustomerDisplay) => void

const CustomerDisplayProvider: FC<PropsWithChildren<Props>> = ({ children }) => {
  const location = useLocation()
  const listeners = useRef<Array<Listener>>([]).current
  const channel = useRef(new BroadcastChannel(CUSTOMER_DISPLAY_CHANNEL)).current

  const emit = useCallback(
    (payload: PayloadCustomerDisplay) => {
      channel.postMessage(payload)
    },
    [channel]
  )

  const addListener = useCallback(
    (callback: (data: PayloadCustomerDisplay) => void) => {
      listeners.push(callback)
    },
    [listeners]
  )

  const handleLocationChange = useCallback(() => {
    if (location.pathname === '/cashier/retail' || location.pathname === '/cashier/cart/retail') {
      return emit({
        type: ActionTypeEnum.PAGE_CHANGE,
        data: {
          page: PageTypeEnum.CART_RETAIL,
        },
      })
    }

    if (location.pathname === '/cashier/cart') {
      return emit({
        type: ActionTypeEnum.PAGE_CHANGE,
        data: {
          page: PageTypeEnum.CART,
        },
      })
    }

    return emit({
      type: ActionTypeEnum.PAGE_CHANGE,
      data: {
        page: PageTypeEnum.STAND_BY,
      },
    })
  }, [emit, location.pathname])

  const value = {
    emit,
    addListener,
  }

  useEffect(() => {
    channel.onmessage = (ev: MessageEvent<PayloadCustomerDisplay>) => {
      listeners.forEach((listener) => {
        listener(ev.data)
      })
    }
  }, [channel, listeners])

  useEffect(() => {
    handleLocationChange()
  }, [handleLocationChange])

  return <CustomerDisplayContext.Provider value={value}>{children}</CustomerDisplayContext.Provider>
}

export { CustomerDisplayProvider }
